import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Check, Crown, Zap, Cloud, MapPin, Bell, 
  Plane, Radio, Sparkles, Calendar, Gift, Timer, Shield,
  Users, Star, Lock, Receipt, CreditCard
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import {
  LiveSubscriberCount,
  GrandfatheredPricingBanner,
  Testimonials,
  ExitIntentPopup,
  ReferralProgram,
  WhatYoureMissing
} from "../components/PremiumFeatures";
import PurchaseHistory from "../components/PurchaseHistory";
import AdminSubscriptions from "../components/AdminSubscriptions";
import UserReviews from "../components/UserReviews";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Subscription() {
  const { user, token, refreshUser } = useAuth();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [pricing, setPricing] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [processingStripe, setProcessingStripe] = useState(false);
  const [stripeCheckoutUrl, setStripeCheckoutUrl] = useState("");
  const [paypalApprovalUrl, setPaypalApprovalUrl] = useState("");
  const [startingTrial, setStartingTrial] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [referralInfo, setReferralInfo] = useState(null);
  const [hasShownExitIntent, setHasShownExitIntent] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [pricingRes, statusRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/subscription/pricing`),
        token ? axios.get(`${API}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }) : Promise.resolve({ data: null }),
        axios.get(`${API}/api/subscription/stats`)
      ]);
      
      setPricing(pricingRes.data);
      if (statusRes.data) {
        setSubscriptionStatus(statusRes.data);
      }
      if (statsRes.data) {
        setSubscriberCount(statsRes.data.monthly_subscribers || statsRes.data.monthly_subscriptions || 0);
      }
      
      // Fetch referral info if logged in
      if (token) {
        try {
          const referralRes = await axios.get(`${API}/api/subscription/referral`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setReferralInfo(referralRes.data);
        } catch (err) {
          console.error("Referral info fetch error:", err.message);
        }
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e) => {
      const isPremiumUser = subscriptionStatus?.is_premium;
      const isTrialUser = subscriptionStatus?.is_trial;
      if (e.clientY <= 0 && !hasShownExitIntent && !isPremiumUser && !isTrialUser) {
        setShowExitIntent(true);
        setHasShownExitIntent(true);
      }
    };
    
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [hasShownExitIntent, subscriptionStatus]);

  const verifyPayment = useCallback(async (paymentId, payerId) => {
    setProcessingPayment(true);
    try {
      const response = await axios.get(`${API}/api/subscription/verify-payment`, {
        params: { paymentId, PayerID: payerId },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success("Welcome to Premium! Your subscription is now active.");
        await refreshUser();
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment verification failed");
    } finally {
      setProcessingPayment(false);
    }
  }, [token, refreshUser, navigate]);

  // Handle PayPal return
  useEffect(() => {
    const paymentId = searchParams.get("paymentId");
    const payerId = searchParams.get("PayerID");
    
    if (paymentId && payerId && token) {
      verifyPayment(paymentId, payerId);
    }
  }, [searchParams, token, verifyPayment]);

  // Fetch pricing and subscription status
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startTrial = async () => {
    if (!token) {
      toast.error("Please sign in to start your free trial");
      navigate("/login");
      return;
    }
    
    setStartingTrial(true);
    try {
      const response = await axios.post(`${API}/api/subscription/start-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      await refreshUser();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not start trial");
    } finally {
      setStartingTrial(false);
    }
  };

  const handleSubscribe = async () => {
    if (!token) {
      toast.error("Please sign in to subscribe");
      navigate("/login");
      return;
    }
    
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/api/subscription/create-paypal-order`, {
        origin: window.location.origin,
        tracking_id: searchParams.get("tracking_id") || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const approvalUrl = response.data.approval_url;
        setPaypalApprovalUrl(approvalUrl);

        const anchor = document.createElement("a");
        anchor.href = approvalUrl;
        anchor.target = "_top";
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        setTimeout(() => {
          window.open(approvalUrl, "_blank", "noopener,noreferrer");
          setProcessingPayment(false);
        }, 1200);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create payment");
      setProcessingPayment(false);
    }
  };

  const handleStripeSubscribe = async () => {
    if (!token) {
      toast.error("Please sign in to subscribe");
      navigate("/login");
      return;
    }

    setProcessingStripe(true);
    try {
      const lookupKey = pricing?.discount_active ? "premium_yearly_discount" : "premium_yearly";
      const response = await axios.post(`${API}/api/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        tracking_id: searchParams.get("tracking_id") || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const checkoutUrl = response.data.checkout_url;
      setStripeCheckoutUrl(checkoutUrl);

      const anchor = document.createElement("a");
      anchor.href = checkoutUrl;
      anchor.target = "_top";
      anchor.rel = "noopener noreferrer";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      setTimeout(() => {
        window.open(checkoutUrl, "_blank", "noopener,noreferrer");
        setProcessingStripe(false);
      }, 1200);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not start Stripe checkout");
      setProcessingStripe(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#050505]' : 'bg-slate-100'}`}>
        <div className="w-8 h-8 border-2 border-current/20 border-t-current rounded-full animate-spin" />
      </div>
    );
  }

  if (processingPayment) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${isDarkMode ? 'bg-[#050505]' : 'bg-slate-100'}`}>
        <div className="w-12 h-12 border-2 border-[#00f2ea]/20 border-t-[#00f2ea] rounded-full animate-spin mb-4" />
        <p className="text-lg">Processing your payment...</p>
      </div>
    );
  }

  const isPremium = subscriptionStatus?.is_premium;
  const isTrial = subscriptionStatus?.is_trial;
  const canAccessPremium = subscriptionStatus?.can_access_premium;

  return (
    <div className={`min-h-screen overflow-x-hidden ${isDarkMode ? 'bg-[#050505]' : 'bg-slate-100'} transition-colors duration-300`}>
      {/* Exit Intent Popup */}
      <ExitIntentPopup 
        isOpen={showExitIntent} 
        onClose={() => setShowExitIntent(false)}
        discount={20}
      />
      
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm" className="rounded-xl">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        {/* Social Proof - Top */}
        {!isPremium && (
          <div className="mb-8">
            <LiveSubscriberCount count={subscriberCount} />
          </div>
        )}

        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00f2ea]/20 to-[#7000ff]/20 rounded-full px-4 py-2 mb-6">
            <Crown className="w-5 h-5 text-yellow-400" />
            <span className="font-semibold">Upgrade to Premium</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4 leading-tight break-words">
            Unlock the Full Power of
            <br />
            <span className="bg-gradient-to-r from-[#00f2ea] to-[#7000ff] bg-clip-text text-transparent break-all sm:break-normal">
              TheForecastTracker
            </span>
          </h1>
          
          <p className="text-lg opacity-70 max-w-2xl mx-auto">
            Get accurate 15-day forecasts, advanced radar predictions, AI insights, and more.
          </p>
        </motion.div>

        {/* Discount Banner */}
        {pricing?.discount_active && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-[#ff0080] to-[#7000ff] rounded-2xl p-6 mb-8 text-center text-white"
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <Gift className="w-6 h-6" />
              <span className="text-2xl font-bold">Holiday Special: 20% OFF!</span>
              <Gift className="w-6 h-6" />
            </div>
            <p className="opacity-90">
              Limited time offer! Save ${(pricing.yearly_price - pricing.discount_price).toFixed(2)} on your first year.
              <br />
              <span className="text-sm">Offer ends May 1, 2026</span>
            </p>
          </motion.div>
        )}

        {/* Current Subscription Status */}
        {canAccessPremium && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-2xl p-6 mb-8 border ${
              isPremium 
                ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30' 
                : 'bg-gradient-to-r from-[#00f2ea]/20 to-[#7000ff]/20 border-[#00f2ea]/30'
            }`}
          >
            <div className="flex items-center gap-3">
              {isPremium ? (
                <>
                  <Crown className="w-8 h-8 text-yellow-400" />
                  <div>
                    <h3 className="text-xl font-bold">You&apos;re a Premium Member!</h3>
                    <p className="opacity-70">
                      {subscriptionStatus.subscription.is_admin_premium 
                        ? "Lifetime premium access (Admin)" 
                        : `Your subscription is active until ${new Date(subscriptionStatus.subscription.subscription_ends).toLocaleDateString()}`
                      }
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Timer className="w-8 h-8 text-[#00f2ea]" />
                  <div>
                    <h3 className="text-xl font-bold">Free Trial Active</h3>
                    <p className="opacity-70">
                      Your trial ends {new Date(subscriptionStatus.subscription.trial_ends).toLocaleDateString()}
                    </p>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-12 min-w-0">
          {/* Free Plan */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <Cloud className={`w-8 h-8 ${isDarkMode ? 'text-white/60' : 'text-black/60'}`} />
              <h2 className="text-2xl font-bold">Free</h2>
            </div>
            
            <div className="mb-6">
              <span className="text-4xl font-black">$0</span>
              <span className="opacity-60">/forever</span>
            </div>
            
            <ul className="space-y-3 mb-8">
              {pricing?.features.free.map((feature, idx) => (
                <li key={`item-${idx}`} className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="opacity-80">{feature}</span>
                </li>
              ))}
            </ul>
            
            <Button 
              variant="outline" 
              className="w-full rounded-xl py-6"
              disabled
            >
              Current Plan
            </Button>
          </motion.div>

          {/* Premium Plan */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="relative glass rounded-2xl p-8 border-2 border-[#00f2ea]/50"
          >
            {/* Popular Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#00f2ea] to-[#7000ff] text-white px-4 py-1 rounded-full text-sm font-semibold">
              MOST POPULAR
            </div>
            
            <div className="flex items-center gap-3 mb-4 pt-2">
              <Crown className="w-8 h-8 text-yellow-400" />
              <h2 className="text-2xl font-bold">Premium</h2>
            </div>
            
            <div className="mb-6">
              {pricing?.discount_active ? (
                <>
                  <span className="text-2xl line-through opacity-40">${pricing?.yearly_price}</span>
                  <span className="text-4xl font-black text-[#ff0080] ml-2">${pricing?.discount_price}</span>
                  <span className="opacity-60">/year</span>
                </>
              ) : (
                <>
                  <span className="text-4xl font-black">${pricing?.yearly_price}</span>
                  <span className="opacity-60">/year</span>
                </>
              )}
            </div>
            
            <ul className="space-y-3 mb-8">
              {pricing?.features.premium.map((feature, idx) => (
                <li key={`item-${idx}`} className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#00f2ea] flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            
            {isPremium ? (
              <Button 
                disabled
                className="w-full rounded-xl py-6 bg-green-500"
              >
                <Check className="w-5 h-5 mr-2" />
                Active Subscription
              </Button>
            ) : (
              <div className="space-y-3">
                {!isTrial && !subscriptionStatus?.subscription?.trial_ends && (
                  <Button 
                    onClick={startTrial}
                    disabled={startingTrial}
                    variant="outline"
                    className="w-full rounded-xl py-6 border-[#00f2ea] text-[#00f2ea] hover:bg-[#00f2ea]/10"
                  >
                    {startingTrial ? (
                      <div className="w-5 h-5 border-2 border-current/20 border-t-current rounded-full animate-spin" />
                    ) : (
                      <>
                        <Timer className="w-5 h-5 mr-2" />
                        Start 7-Day Free Trial
                      </>
                    )}
                  </Button>
                )}
                
                <Button 
                  onClick={handleStripeSubscribe}
                  disabled={processingStripe || processingPayment}
                  data-testid="stripe-subscribe-button"
                  className="w-full rounded-xl py-6 font-semibold bg-gradient-to-r from-[#635bff] to-[#00f2ea] hover:opacity-90 text-white"
                >
                  {processingStripe ? (
                    <div className="w-5 h-5 border-2 border-current/20 border-t-current rounded-full animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Subscribe with Stripe
                      {pricing?.discount_active && " - 20% OFF"}
                    </>
                  )}
                </Button>

                {stripeCheckoutUrl && (
                  <a
                    href={stripeCheckoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center text-sm text-[#00f2ea] hover:underline"
                    data-testid="stripe-manual-checkout-link"
                  >
                    Checkout not opening? Open Stripe in a new tab
                  </a>
                )}

                <Button 
                  onClick={handleSubscribe}
                  disabled={processingPayment || processingStripe}
                  data-testid="paypal-subscribe-button"
                  className={`w-full rounded-xl py-6 font-semibold ${
                    isDarkMode 
                      ? 'bg-[#00f2ea] hover:bg-[#00d4d4] text-black' 
                      : 'bg-cyan-600 hover:bg-cyan-700 text-white'
                  }`}
                >
                  {processingPayment ? (
                    <div className="w-5 h-5 border-2 border-current/20 border-t-current rounded-full animate-spin" />
                  ) : (
                    <>
                      <Zap className="w-5 h-5 mr-2" />
                      Subscribe with PayPal
                      {pricing?.discount_active && " - 20% OFF"}
                    </>
                  )}
                </Button>
                {paypalApprovalUrl && (
                  <a
                    href={paypalApprovalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center text-sm text-[#00f2ea] hover:underline"
                    data-testid="paypal-manual-checkout-link"
                  >
                    PayPal not opening? Open PayPal in a new tab
                  </a>
                )}
              </div>
            )}
          </motion.div>
        </div>

        {/* Features Grid */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-center mb-8">Premium Features</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Calendar, title: "15-Day Forecasts", desc: "Plan ahead with extended weather predictions" },
              { icon: MapPin, title: "3-Day Radar Outlook", desc: "See future radar predictions hour by hour" },
              { icon: Sparkles, title: "AI Weather Insights", desc: "Get intelligent summaries of weather conditions" },
              { icon: Bell, title: "Severe Weather Alerts", desc: "Real-time notifications for dangerous weather" },
              { icon: Plane, title: "Flight Weather", desc: "Check conditions for your travel plans" },
              { icon: Radio, title: "Storm Chaser Feeds", desc: "Access live storm tracking and streams" },
            ].map((feature, idx) => (
              <div key={`item-${idx}`} className="glass rounded-xl p-6">
                <feature.icon className={`w-8 h-8 ${isDarkMode ? 'text-[#00f2ea]' : 'text-cyan-600'} mb-3`} />
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm opacity-70">{feature.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Grandfathered Pricing Promise */}
        {!isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8"
          >
            <GrandfatheredPricingBanner />
          </motion.div>
        )}

        {/* Testimonials & What You're Missing - Side by Side */}
        {!isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid md:grid-cols-2 gap-8 mb-12"
          >
            <Testimonials />
            <WhatYoureMissing />
          </motion.div>
        )}

        {/* Referral Program */}
        {token && referralInfo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-12"
          >
            <ReferralProgram 
              referralCode={referralInfo.referral_code}
              referralCount={referralInfo.referral_count}
              referralLink={referralInfo.referral_link}
            />
          </motion.div>
        )}

        {/* User Reviews Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className={`mb-12 p-6 rounded-2xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
        >
          <UserReviews token={token} user={user} isDarkMode={isDarkMode} />
        </motion.div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-6 mb-8 opacity-60">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            <span className="text-sm">Secure Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5" />
            <span className="text-sm">Cancel Anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <Timer className="w-5 h-5" />
            <span className="text-sm">7-Day Free Trial</span>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm opacity-50">
          <p>By subscribing, you agree to our <Link to="/terms" className="underline">Terms of Service</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
        </div>
      </div>
    </div>
  );
}
